import { cloneDeep } from 'lodash';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DashboardLoader,
  downloadFile,
  FilterLayout,
  FilterProp,
  formatRelativeTimeForTable,
  renderHeaderActionButtons,
  SidePopover,
  TableDashboard,
  useAppDispatch,
  useAppSelector,
} from '../../globalUtils/globalExports';
import {
  getReportReducer,
  fetchSessionHistoryReportData,
  setReportListTableProps,
  reportTypes,
  getFilterSelectedValue,
  getReferenceDataReducer,
  getFilterReducer,
  setFilterData,
  getCurrentFiltersValues,
  exportSessionHistoryReport,
  fetchChargerVisibleIdReferenceData,
  fetchChargerProductTypeReferenceData,
  getCurrentScreenAppliedFiltersCount,
  setRedirectedStateForSessionHistoryFilters,
  fetchChargerVisibleIdsPaginatedList,
} from '../../rmsReduxStore/reduxExports';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import './reports.css';
import { Id, toast } from 'react-toastify';
import { NoDataComponentSessionHistory } from '../../globalUtils/TableDashboard/TableComponents';
import access from '../../auth/service/AccessControl';
import { IconWithTooltip } from '../../globalUtils/globalIcons';
import { useNavigate } from 'react-router-dom';

const SessionHistoryDashboard = memo(function SessionHistoryDashboard() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sessionHistoryColumnHeaders,sessionHistoryData, sessionHistoryCount, reportLoader, sessionHistoryListTableProps,sessionHistoryFiltersAppliedFromRedirection } = useAppSelector(getReportReducer);
  const { chargerVisibleIdReferenceData,chargingStationReferenceData,chargingHistoryStopReasonCodeReferenceData ,chargerProductTypeReferenceData  } = useAppSelector(getReferenceDataReducer);
  const { screenFilters } = useAppSelector(getFilterReducer)
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [exportingFile, setExportingFile] = useState(false);
  const [dashboardLoader, setDashboardLoader] = useState(false)
  const sessionHistoryListTablePropsRefs = useRef(sessionHistoryListTableProps)
  const isOrgTypeCustomer = access?.isOrgTypeCustomer();
  const [filtersVisible,setFiltersVisible] = useState<boolean>(false);
  useEffect(() => {
    sessionHistoryListTablePropsRefs.current = sessionHistoryListTableProps;
  }, [sessionHistoryListTableProps])

  // TODO: Handles global search box
  const onChangeOfSearchText = useCallback(async (searchTerm) => {
    if (sessionHistoryListTablePropsRefs) {
      await layoutCallback(1, sessionHistoryListTablePropsRefs?.current?.pageSize, sessionHistoryListTablePropsRefs?.current?.view, sessionHistoryListTablePropsRefs?.current?.sortBy, sessionHistoryListTablePropsRefs?.current?.order, null, searchTerm)
    }
  }, [sessionHistoryListTablePropsRefs, sessionHistoryColumnHeaders, sessionHistoryData]);

  const searchBoxConfiguration = useMemo(() => {
    return {
      searchFieldId: 'manage-session-history-search-box',
      searchFieldName: 'manage-session-history-search-box',
      debounceTime:600,
      handleSearch: onChangeOfSearchText,
    };
  }, [onChangeOfSearchText]);

  // TODO: Adds action btns in header and their callbacks
  const headerActionBtns = renderHeaderActionButtons({
    actionBtns:
      [
        {
          id: 'configure_stop_reasons_btn',
          isAccessible: access?.hasPlcConfigStopReasonsAccess(),
          headerActionBtnClick: (): void => {
            navigate('/configure-plc-logs')
          },
        },
        {
          id: 'add_filter_btn',
          isAccessible: true,
          headerActionBtnClick: (): void => {
            setFiltersVisible(true);
          },
          badgeCount: getCurrentScreenAppliedFiltersCount('sessionHistoryReport')
        },
        {
          id: 'export_csv_btn',
          isAccessible:isOrgTypeCustomer ? true: access?.reports?.chargingHistory?.export(),
          headerActionBtnClick: (event): void => {
            event?.stopPropagation()
            setExportAnchorEl(event.currentTarget);
          }
        }
      ]
  })

  // TODO: Table Layout Callbacks
  const mobileViewConfiguration = useMemo(() => {
    return {
      headerDataConfig: {
        headerLeftDataConfig: ['name', 'type'],
        headerRightDataConfig: {
          actionIconsComponent: false,
          statusIconComponent: true,
        },
      },
      statusIconKey: 'status',
      contentDataConfig: [
        [],
      ],
    };
  }, []);

  const getTableRows = useCallback(() => {
    if (!sessionHistoryData || !sessionHistoryColumnHeaders) {
      return [];
    }
    const data = cloneDeep(sessionHistoryData)
    sessionHistoryColumnHeaders?.forEach((column) => {
      const { key, dataType, unit } = column;
      if (key && (dataType || unit)) {

        data?.forEach((historyItem) => {
          if (historyItem?.[key] && dataType === 'Date' && historyItem[key] !== 'NA') {
            historyItem[key] = formatRelativeTimeForTable(historyItem[key]);
          }
          if (unit && historyItem[key] !== 'NA') {
            historyItem[key] = `${historyItem[key]} ${unit}`;
          }
        })
      }
    })
    return data || [];
  }, [sessionHistoryData, sessionHistoryColumnHeaders]);

const renderUploadStatusCell = (params) => {
  const { uploadStatus, sessionLogFileUrl } = params.row;

  if (!uploadStatus && !sessionLogFileUrl) return <span>-</span>;

  if (sessionLogFileUrl && uploadStatus === 'Completed') {
    return (
      <div
        className="rms__support__documentation__action__icon__wrap"
        style={{ display: 'flex', justifyContent: 'center' ,cursor:'pointer' }}
        onClick={() => {
          try {
            downloadFile({ url: sessionLogFileUrl });
            toast.success('File downloaded.');
          } catch {
            toast.error('Download failed.');
          }
        }}
      >
        <IconWithTooltip iconClassName="rms__documentation__download__icon" toolTipText="Download PLC Log File" />
      </div>
    );
  }

  if (uploadStatus === 'Failed') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
        <span style={{ backgroundColor: '#D32F2F', color: '#fff', borderRadius: '6px', padding: '3px 14px', fontSize: '13px', fontWeight: 500}}>Failed</span>
      </div>
    );
  }

    if (uploadStatus === 'Pending' || uploadStatus == 'Uploading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
        <span style={{backgroundColor: 'rgba(68, 142, 247, 1)', color: '#fff', borderRadius: '6px', padding: '3px 14px', fontSize: '13px', fontWeight: 500}}>Fetching</span>
      </div>
    );
  }
  return <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>-</div>;
};

const sessionHistoryColumns = sessionHistoryColumnHeaders?.map((item) => {
  const base = {
    field: item?.key,
    description: item?.description,
    flex: 1,
    headerName: item?.visibleName,
    sortable: item?.key === 'beginTime',
    minWidth: item?.visibleName?.length > 25 ? 200 : 150,
    pinnable: false,
  };

  return item?.key === 'uploadStatus'
    ? { ...base, renderCell: renderUploadStatusCell }
    : base;
});



  const layoutCallback = useCallback(async (pageNumber: number, pageSize: number, view: string, sortField: string, sortOrder: string, filterValue, searchTerm) => {
    const tableProps: IListTableProps = cloneDeep(sessionHistoryListTablePropsRefs.current)
    await dispatch(fetchSessionHistoryReportData({
      sortBy: sortField || '',
      order: sortOrder || 'ascending',
      pageSize,
      pageNumber,
      filters: filterValue ? filterValue : getCurrentFiltersValues('sessionHistoryReport'),
      searchTerm: searchTerm != null && searchTerm != undefined ? searchTerm : sessionHistoryListTablePropsRefs?.current?.searchTerm
    }));
    if (tableProps) {
      tableProps.view = view || 'list'
      tableProps.sortBy = sortField || ''
      tableProps.order = sortOrder || 'ascending';
      tableProps.pageNumber = pageNumber;
      tableProps.pageSize = pageSize;
      if (searchTerm != null && searchTerm != undefined) {
        tableProps.searchTerm = searchTerm;
      }
    }
    await dispatch(setReportListTableProps(tableProps, 'sessionHistory'))
  }, [sessionHistoryListTablePropsRefs, dispatch]);

  // TODO: Redux state Clean up
  const sessionHistoryCleanUpStates = useCallback(() => {
    const action = {
      type: reportTypes.CLEAR_SESSION_HISTORY_LIST_DATA,
    }
    dispatch(action)
    if (sessionHistoryFiltersAppliedFromRedirection) {
        onResetButtonClick(true)
    }
    setRedirectedStateForSessionHistoryFilters(false)(dispatch)
  }, [sessionHistoryFiltersAppliedFromRedirection])

  useEffect(() => {
    window.addEventListener('beforeunload', sessionHistoryCleanUpStates);
    return (): void => {
      sessionHistoryCleanUpStates()
      window.removeEventListener('beforeunload', sessionHistoryCleanUpStates);
    }
  }, [])

  // TODO: Export Handlers
  const handleClose = (): void => {
    setExportAnchorEl(null);
  };

  const fileDownloadSuccessToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.success('File downloaded.');
  }, []);

  const fileDownloadFailureToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.warn('Something went wrong. Please export again.');
  }, []);

  const renderExportMenuItem = (fileType: 'csv' | 'xlsx', label: string): JSX.Element => (
    <MenuItem key={fileType}>
      <div className='export__btn-option' onClick={(e) => {
        e?.stopPropagation();
        onExportBtnClick(fileType);
      }}>
        {label}
      </div>
    </MenuItem>
  );

  const dateRangeExceededErrorToast = (message): Id => toast.error(message);

  const dateFilterRangeCheck = (daysLimit) => {
    const filterSetOnValue = getFilterSelectedValue('chargingSessionHistoryInterval', 'sessionHistoryReport', false);
    const startDate = filterSetOnValue?.[0] ? new Date(filterSetOnValue?.[0]) : null;
    const endDate = filterSetOnValue?.[1] ? new Date(filterSetOnValue?.[1]) : null;
    if (startDate && endDate) {
      const differenceInMilliseconds = endDate.getTime() - startDate.getTime();
      const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);
      if (differenceInDays < daysLimit) {
        return true;
      }
    }
    return false;
  }

  const onExportBtnClick = useCallback(async (fileType: 'csv' | 'xlsx'): Promise<void> => {
    if (!dateFilterRangeCheck(5)) {
      const filterSetOnValue = getFilterSelectedValue('chargingSessionHistoryInterval', 'sessionHistoryReport', false);
      const startDate = filterSetOnValue?.[0] ? filterSetOnValue?.[0] : null;
      const endDate = filterSetOnValue?.[1] ? filterSetOnValue?.[1] : null;
      if (startDate && endDate) dateRangeExceededErrorToast('Date range exceeded more than 5 days!!')
      else dateRangeExceededErrorToast('Please select date range of 5 days!!')
      return;
    }

    if (exportingFile) {
      // If already exporting, prevent another export
      return;
    }
    try {
      setDashboardLoader(true)
      const response = await dispatch(exportSessionHistoryReport({
        fileType: fileType,
        filters: getCurrentFiltersValues('sessionHistoryReport')
      }));
      if (response?.status === 202 || response?.status === 200) {
        setDashboardLoader(false)
        downloadFile({ url: response?.data?.url });
        fileDownloadSuccessToast()
      } else {
        setDashboardLoader(false)
        fileDownloadFailureToast();
        setExportAnchorEl(null);
      }
    } finally {
      setDashboardLoader(false)
      // Ensure that exporting status is updated even if there's an error
      setExportingFile(false);
      setExportAnchorEl(null);
    }
  }, [exportingFile, fileDownloadSuccessToast, fileDownloadFailureToast])

  // TODO: Filter side pop up 
  const handleFilterDataChange = useCallback(async (val, filterKey, isGlobal) => {
    if (isGlobal && filterKey === 'customer') {
      const reqBody = {
        customerIds : val?.filter((item) => item?.id === 'all')?.length > 0  ? ['all'] : val?.map((customer) => customer?.id)
      }
      dispatch(fetchChargerVisibleIdReferenceData(reqBody))
    }
    const res = await setFilterData(val, filterKey, 'sessionHistoryReport', 'SET', isGlobal ? isGlobal : false)(dispatch)
    if (res?.message === 'Action dispatched successfully') {
        const filterSetOnValue = getFilterSelectedValue('chargingSessionHistoryInterval', 'sessionHistoryReport', false);
        const startDate = filterSetOnValue?.[0] ? filterSetOnValue?.[0] : null;
        const endDate = filterSetOnValue?.[1] ? filterSetOnValue?.[1] : null;
        if (startDate && endDate) {
          const differenceInMilliseconds = endDate.getTime() - startDate.getTime();
          const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);
          if (differenceInDays > 6) {
            dateRangeExceededErrorToast('Date range exceeded more than 5 days!!')
            return
          }
        } else if (startDate && !endDate) {
          dateRangeExceededErrorToast('Please select date range of 5 days!!')
          return
        }
        if (sessionHistoryListTablePropsRefs?.current) {
          await layoutCallback(1, sessionHistoryListTablePropsRefs?.current?.pageSize, sessionHistoryListTablePropsRefs?.current?.view, sessionHistoryListTablePropsRefs?.current?.sortBy, sessionHistoryListTablePropsRefs?.current?.order, res?.filters, sessionHistoryListTablePropsRefs?.current?.searchTerm)
        }
    }
  }, [sessionHistoryData, dispatch],)

  const plcLogFileStatusOptions = [
    { id: 'Fetching', label: 'Fetching', value: 'Fetching', name: 'Fetching' },
    { id: 'Failed', label: 'Failed', value: 'Failed', name: 'Failed' },
    { id: 'Completed', label: 'Completed', value: 'Completed', name: 'Completed' },
  ];

  //TODO: Include Date Intervals for dateFilter
  const sessionHistoryFilterProps: FilterProp[] = useMemo(() => {
    const filterSetOnValue = getFilterSelectedValue('chargingSessionHistoryInterval', 'sessionHistoryReport', false);

    return [
      {
        filterLabel: 'Charging Stations',
        filterType: 'dropdown',
        filterId: 'chargingStationId',
        filterDropdownProps: {
          selectValue: getFilterSelectedValue('chargingStationId', 'sessionHistoryReport', false),
          selectOptions: chargingStationReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'chargingStationId', false)
          },
          selectDropDownId: 'session-history-chargingStationId-type-filter-dropdown',
          isMultiSelect: true
        }
      },
      {
        filterLabel: 'Chargers',
        filterType: 'asyncDropdown',
        filterId: 'chargerId',
        asyncDropdownProps: {
            fetchOptions: (search: string, offset: number, limit: number) =>
                dispatch(fetchChargerVisibleIdsPaginatedList({ search, offset, limit })),
            selected: getFilterSelectedValue('chargerId', 'sessionHistoryReport', false),
            onSelectedChange: (val): void => {
                handleFilterDataChange(val, 'chargerId', false)
            },
            selectDropDownId: 'session-history-chargerId-filter-dropdown',
            isMultiSelect: true
        }
      },
      {
        filterLabel: 'Product Type',
        filterType: 'dropdown',
        filterId: 'productType',
        filterDropdownProps: {
        selectValue: getFilterSelectedValue('productType', 'sessionHistoryReport', false),
        selectOptions: chargerProductTypeReferenceData ?? [],
        handleSelectOptionChange: (val): void => {
        handleFilterDataChange(val, 'productType', false)
        },
        selectDropDownId: 'charger-charger-type-filter-dropdown',
        isMultiSelect: true
        }
      },
      {
        filterLabel: 'Stop Reason Code',
        filterType: 'dropdown',
        filterId: 'chargingSessionHistoryStopReason',
        filterDropdownProps: {
          selectValue: getFilterSelectedValue('chargingSessionHistoryStopReason', 'sessionHistoryReport', false),
          selectOptions: chargingHistoryStopReasonCodeReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'chargingSessionHistoryStopReason', false)
          },
          selectDropDownId: 'session-history-stop-reason-code-filter-dropdown',
          isMultiSelect: true,
        }
      },
      {
        filterLabel: 'Plc Log File Status',
        filterType: 'dropdown',
        filterId: 'plcLogFileStatus',
        filterDropdownProps: {
          selectValue: getFilterSelectedValue('plcLogFileStatus', 'sessionHistoryReport', false),
          selectOptions: plcLogFileStatusOptions,
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'plcLogFileStatus', false)
          },
          selectDropDownId: 'session-history-plc-log-file-status-filter-dropdown',
          isMultiSelect: true,
        }
      },
      {
        filterLabel: 'Interval',
        filterType: 'dateFilter',
        filterId: 'chargingSessionHistoryInterval',
        dateFilterProps: {
          datePickerId: 'session__history__time__stamp__set__at__date__picker',
          datePickerClassName: 'session__history__time__stamp__cleared__at__date__picker',
          selectsRange: true,
          startDate: filterSetOnValue?.[0] ? new Date(filterSetOnValue?.[0]) : null,
          endDate: filterSetOnValue?.[1] ? new Date(new Date(filterSetOnValue?.[1]).getTime()) : null,    
          onChange: (val): void => {
            handleFilterDataChange(val, 'chargingSessionHistoryInterval', false)
          },
        }
      },
    ];
  }, [chargerVisibleIdReferenceData,chargingHistoryStopReasonCodeReferenceData  ,chargingStationReferenceData ,screenFilters,chargerProductTypeReferenceData]);

  const handleFiltersClosePopOver = (): void => {
    setFiltersVisible(false);
  }

  const onResetButtonClick = useCallback(async (callList: boolean) => {
      const res = await setFilterData(null, '', 'sessionHistoryReport', 'CLEAR',false)(dispatch);
      if (res?.message === 'Action dispatched successfully') {
        // Reset the filters
        if (callList && sessionHistoryListTablePropsRefs?.current) {
          const tableProps = cloneDeep(sessionHistoryListTablePropsRefs?.current);
          await layoutCallback(
            1,
            tableProps?.pageSize,
            tableProps?.view,
            tableProps?.sortBy,
            tableProps?.order,
            res?.filters,
            tableProps?.searchTerm ?? '',
          )
        }
      }
  
    }, [sessionHistoryListTablePropsRefs?.current])

  const filterFooterBtns = useMemo(() => {
      return [
        {
          buttonText: 'Reset',
          buttonId: 'Reset',
          btnClassName: 'secondary__btn',
          handleClick: (): Promise<void> => onResetButtonClick(true),
          isDisabled: false,
          buttonVariant: 'outlined',
        },
      ];
    }, [onResetButtonClick]);

  useEffect(()=>{
  dispatch(fetchChargerProductTypeReferenceData());
  },[])

  return (
    <>

      <DashboardLoader showLoader={dashboardLoader} />
      <TableDashboard
        tableId="manage-session-history-table"
        tableClassName="manage__session__history__table"
        header={'Session History'}
        searchBoxIncluded={true}
        searchBoxConfiguration={searchBoxConfiguration}
        gridColumns={sessionHistoryColumns}
        tableRows={getTableRows()}
        mobileViewConfiguration={mobileViewConfiguration}
        layoutView={'list'}
        headerActionBtns={headerActionBtns}
        listLayoutCallBack={layoutCallback}
        totalCount={sessionHistoryCount}
        showSkeleton={!sessionHistoryData}
        totalCountText={'session history'}
        showLoader={reportLoader}
        customNoDataComponent={NoDataComponentSessionHistory}
        pageNumber={sessionHistoryListTableProps?.pageNumber}
        tablePageSize={sessionHistoryListTableProps?.pageSize}
        // filterProps={chargingHistoryFilterProps}
        // handleFilterDataChange={handleFilterDataChange}
        referenceDataCallbacksFilters={['chargerVisibleId']}
        leftPinnedColumns={['chargerVisibleId','connectorNo']}
        rightPinnedColumns={['uploadStatus']}
        enableCustomPagination
        screen = 'session_history'
      />
      <Menu
        id="export-option-dropdown"
        anchorEl={exportAnchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className='export__option__dropdown'
        sx={{ top: '30px' }}
        open={Boolean(exportAnchorEl)}
        onClose={handleClose}
      >
        {renderExportMenuItem('csv', 'To CSV')}
        {renderExportMenuItem('xlsx', 'To Excel')}
      </Menu>

       <>
        {
          filtersVisible && <SidePopover
            id={'manage-version-releases-filters'}
            open={filtersVisible}
            containerClassName={'version-releases-side-filters'}
            handleClose={handleFiltersClosePopOver}
            headerText={'Add Filters'}
            // eslint-disable-next-line react/no-children-prop
            children={<FilterLayout requiredReferenceDataCallbacks={['chargerVisibleId', 'chargingHistoryStopReasonCode', 'customer','chargingStationId']}
              filters={sessionHistoryFilterProps} handleFilterDataChange={handleFilterDataChange}
            />}
            btnsList={filterFooterBtns}
          />
        }
      </>
      {/* <>
        {
          stopReasonConfigVisible && <SidePopover
            id={'configure-stop-reasons-popover'}
            open={stopReasonConfigVisible}
            containerClassName={'version-releases-side-filters'}
            handleClose={(): void => setStopReasonConfigVisible(false)}
            horizontalLinePopover= {true}
            headingIconClassName={'rms_settings_icon'}
            headingIconFirst={true}
            headerText={'Configure Stop Reasons'}
            // eslint-disable-next-line react/no-children-prop
            children={<StopReasonConfigSidePopover handleClose={(): void => setStopReasonConfigVisible(false)} />}
          />
        }
      </> */}
    </>
  );
})

export default SessionHistoryDashboard;